import { useQuery } from '@apollo/client'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { type GetRostersByNameQuery } from '__generated__/graphql'
import { useState, type FC } from 'react'
import {
    GetRostersByIds,
    GetRostersByName,
} from 'shared/components/RostersAutocomplete/RostersAutocomplete.gql'
import { useReferenceSearchesUrlState } from 'shared/hooks/useReferenceSearchesUrlState'
import { useRosterSearch } from './columns/RosterSearchContext'

type Roster = NonNullable<
    GetRostersByNameQuery['rostersByName']
>['data'][number]

const EMPTY_ROSTERS: Roster[] = []

const inputSx = {
    '& .MuiOutlinedInput-root': {
        fontSize: '0.75rem',
        py: 0,
        '& fieldset': { borderColor: 'divider' },
        '&:hover fieldset': { borderColor: 'text.secondary' },
    },
}

const tagBaseSx = {
    px: 1,
    py: 0.25,
    borderRadius: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    cursor: 'pointer',
    fontSize: '0.6875rem',
    height: 24,
    bgcolor: 'primary.dark',
    color: 'white',
    border: '1px solid',
    borderColor: 'primary.dark',
    '&:hover': {
        bgcolor: 'grey.200',
        color: 'text.primary',
    },
}

const renderTags = (value: Roster[]) =>
    value.length > 0 ? (
        <Typography component="span" sx={tagBaseSx}>
            {value.length} selected
        </Typography>
    ) : null

export const ReferenceSearchesSelector: FC = () => {
    const { rosterId } = useRosterSearch()
    const { refColumnRosterIds, setRefColumnRosterIds } =
        useReferenceSearchesUrlState()
    const [open, setOpen] = useState(false)
    const [searchText, setSearchText] = useState('')

    const { data: selectedRostersData } = useQuery(GetRostersByIds, {
        variables: { ids: refColumnRosterIds },
        skip: refColumnRosterIds.length === 0,
    })
    const selectedRosters = selectedRostersData?.rostersByIds ?? EMPTY_ROSTERS

    const {
        data: searchData,
        loading,
        previousData: previousSearchData,
    } = useQuery(GetRostersByName, {
        variables: {
            name: searchText,
            limit: 20,
            currentRosterId: rosterId,
        },
        skip: !open,
    })

    const searchResults =
        (searchData ?? previousSearchData)?.rostersByName?.data ?? []
    const options = searchResults.filter((r) => r.id !== rosterId)

    return (
        <Box width={200}>
            {open && (
                <Box
                    onClick={() => setOpen(false)}
                    sx={{ position: 'fixed', inset: 0, zIndex: 1299 }}
                />
            )}
            <Autocomplete
                multiple
                size="small"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (
                        reason !== 'selectOption' &&
                        reason !== 'removeOption'
                    ) {
                        setOpen(false)
                    }
                }}
                loading={loading}
                options={options}
                value={selectedRosters as Roster[]}
                inputValue={searchText}
                onInputChange={(_, value) => setSearchText(value)}
                getOptionLabel={(r) => r.rosterName}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText={
                    <Typography sx={{ fontSize: '0.75rem' }}>
                        No options
                    </Typography>
                }
                filterOptions={(x) => x}
                onChange={(_, newVal) => {
                    setRefColumnRosterIds(newVal.map((r) => r.id))
                    setSearchText('')
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Reference Searches"
                        sx={inputSx}
                    />
                )}
                renderTags={renderTags}
                renderOption={(
                    { key, ...optionProps },
                    roster,
                    { selected }
                ) => (
                    <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{
                            fontSize: '0.75rem',
                            py: 0.5,
                            minHeight: 'unset',
                            bgcolor: selected ? 'primary.50' : 'inherit',
                            borderLeft: selected ? '3px solid' : 'none',
                            borderLeftColor: 'primary.main',
                        }}
                    >
                        {roster.rosterName}
                    </Box>
                )}
            />
        </Box>
    )
}
